<template>
  <div>
    <div v-if="showTenantPreWorkWarning" class="warning-message mb-2">
      <i class="far fa-exclamation-triangle mr-1"></i>
      Accepted/Pre-Work time is currently requested by the company for Job Time
      Efficiency calculations.
    </div>
    <div v-if="showWarning" class="warning-message mb-2">
      <i class="far fa-exclamation-triangle mr-1"></i> Changing the status will
      clear actual time(s), affecting efficiency. <br />
      <div v-if="jobId" class="schedule-link">
        Would you prefer to
        <a :href="openJobPage">
          <i class="far fa-calendar-alt mx-1"></i> Schedule a New Appointment</a
        >?
      </div>
    </div>
    <div>
      <slot name="label"></slot>
      <div class="container px-0">
        <div
          class="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x *:md:pl-4 gap-4 *:pt-4"
        >
          <div class="pt-0 pl-0">
            <div
              data-testid="accepted-time-lost"
              :class="{ 'lost-time': timeIsLost('accepted_time') }"
            >
              <time-picker
                v-model="accepted"
                data-testid="accepted-time-time-picker"
                :field-error="v$.accepted.$errors[0]?.$message"
                label="Accepted"
                placeholder="--"
                :name="generateValidationId('accepted_time')"
                data-vv-as="Accepted"
                :disabled="
                  isDisabled('accepted_time') || timeIsLost('accepted_time')
                "
                :format="timeFormat"
                @clear-date="clearTime('accepted')"
              >
                <template #date>
                  <div class="date-to-display">
                    <span>Date:</span>
                    {{ shortDate(accepted || dispatched || scheduledTime) }}
                  </div>
                </template>
              </time-picker>
            </div>
            <appointment-time-label-v2
              label="Pre-Work"
              :minutes="preworkTime"
              :time-lost="preworkLost"
              :next-day="preworkNextDay"
              default-text="No Travel Started Time Set"
              :scheduled-time="scheduledTime"
            ></appointment-time-label-v2>
          </div>
          <div>
            <div :class="{ 'lost-time': timeIsLost('dispatch_time') }">
              <time-picker
                v-model="dispatched"
                data-testid="time-picker-dispatched"
                :field-error="v$.dispatched.$errors[0]?.$message"
                :label="'Travel Started'"
                placeholder="--"
                :name="generateValidationId('dispatch_time')"
                :data-vv-as="'Travel Started'"
                :disabled="
                  isDisabled('dispatch_time') || timeIsLost('dispatch_time')
                "
                :format="timeFormat"
                @clear-date="clearTime('dispatched')"
              >
                <template #date>
                  <div class="date-to-display">
                    <span>Date:</span>
                    {{ shortDate(dispatched || scheduledTime) }}
                  </div>
                </template>
              </time-picker>
            </div>
            <appointment-time-label-v2
              label="Travel"
              :minutes="travelTime"
              :time-lost="travelLost"
              :next-day="travelNextDay"
              default-text="No Arrival Time Set"
              :scheduled-time="scheduledTime"
            ></appointment-time-label-v2>
          </div>
          <div>
            <div
              class="flex-1"
              :class="{ 'lost-time': timeIsLost('arrival_time') }"
            >
              <time-picker
                v-model="arrival"
                :field-error="v$.arrival.$errors[0]?.$message"
                label="Arrival"
                placeholder="--"
                :name="generateValidationId('arrival_time')"
                data-vv-as="Arrival"
                :disabled="
                  isDisabled('arrival_time') || timeIsLost('arrival_time')
                "
                :format="timeFormat"
                @clear-date="clearTime('arrival')"
              >
                <template #date>
                  <div class="date-to-display">
                    <span>Date:</span>
                    {{ shortDate(arrival || scheduledTime) }}
                  </div>
                </template>
              </time-picker>
            </div>
            <appointment-time-label-v2
              label="Work"
              :minutes="workTime"
              :time-lost="workLost"
              :next-day="workNextDay"
              default-text="No Departure Time Set"
            ></appointment-time-label-v2>
          </div>
          <div>
            <div
              class="flex-1"
              :class="{ 'lost-time': timeIsLost('departure_time') }"
            >
              <time-picker
                v-model="departure"
                label="Departure"
                :field-error="v$.departure.$errors[0]?.$message"
                placeholder="--"
                :name="generateValidationId('departure_time')"
                data-vv-as="Departure"
                :disabled="
                  isDisabled('departure_time') || timeIsLost('departure_time')
                "
                :format="timeFormat"
                @clear-date="clearTime('departure')"
              >
                <template #date>
                  <div class="date-to-display">
                    <span>Date:</span>
                    {{ shortDate(departure || scheduledTime) }}
                  </div>
                </template>
              </time-picker>
            </div>
            <appointment-time-label-v2
              :class="{ 'text-danger': isWorkTravelsNegative }"
              label="Duration"
              :minutes="duration"
              :time-lost="durationLost"
              default-text="Appointment is Not Complete"
              no-icon
            ></appointment-time-label-v2>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { nextTick } from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import {
  currentTenantTimezone,
  generateUID,
  getMinuteDiff,
  getProperty
} from "@/lib/helpers.js";
import { modifyDate } from "@/lib/helpers.js";
import { mustNotBeFutureDate } from "@/lib/vuelidate_helpers";

import AppointmentTimeLabelV2 from "@/components/AppointmentTimeLabelV2.vue";
import TimePicker from "@/components/TimePicker.vue";
import { shortDate } from "@/mixins";
import useVuelidate from "@vuelidate/core";
import { helpers, requiredIf } from "@vuelidate/validators";

export default {
  components: { TimePicker, AppointmentTimeLabelV2 },
  props: {
    acceptedTime: {
      type: String,
      default: ""
    },
    dispatchTime: {
      type: String,
      default: ""
    },
    arrivalTime: {
      type: String,
      default: ""
    },
    departureTime: {
      type: String,
      default: ""
    },
    scheduledTime: {
      type: String,
      default: new Date().toISOString()
    },
    label: {
      type: String,
      default: ""
    },
    lostTimes: {
      type: Array,
      default: () => []
    },
    disabledFields: {
      type: Array,
      default: () => []
    },
    jobId: {
      type: Number,
      default: null
    },
    timezone: {
      type: String,
      default: currentTenantTimezone
    },
    apptState: {
      type: String,
      default: ""
    }
  },
  emits: ["change"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      ids: {
        accepted_time: "",
        dispatch_time: "",
        arrival_time: "",
        departure_time: ""
      }
    };
  },
  computed: {
    ...mapState({
      tenant: "tenant",
      fetchingTenant: "fetchingTenant",
      secondsEnabled({ tenant }) {
        if (!getProperty(tenant, "feature_flags")) {
          return false;
        }
        return tenant.feature_flags["allow_edit_appointment_time_seconds"];
      },
      timeFormat() {
        return this.secondsEnabled ? "h:mm:ss a" : "h:mm a";
      },
      dateFormat() {
        return this.secondsEnabled
          ? "MM/DD/YYYY h:mm:ss a"
          : "MM/DD/YYYY h:mm a";
      },
      tenantPreworkEnabled({ tenant }) {
        if (!getProperty(tenant, "pre_work_enabled")) {
          return false;
        }
        return tenant["pre_work_enabled"];
      }
    }),
    ...mapGetters(["settingEnabledForTenant"]),
    isWorkTravelsNegative() {
      return this.travelTime < 0 || this.workTime < 0;
    },
    preworkTime() {
      if (!this.accepted || !this.dispatched) {
        return null;
      }
      return getMinuteDiff(this.dispatched, this.accepted);
    },
    travelTime() {
      if (!this.dispatched || !this.arrival) {
        return null;
      }
      return getMinuteDiff(this.arrival, this.dispatched);
    },
    workTime() {
      if (!this.arrival || !this.departure) {
        return null;
      }
      return getMinuteDiff(this.departure, this.arrival);
    },
    duration() {
      // TODO: accepted or dispatched, whichever exists etc
      if (!this.dispatched || !this.departure) {
        return null;
      }
      return getMinuteDiff(this.departure, this.accepted || this.dispatched);
    },
    preworkNextDay() {
      if (!this.accepted || !this.dispatched) {
        return false;
      }
      return moment(this.dispatched).day() > moment(this.accepted).day();
    },
    travelNextDay() {
      if (!this.arrival || !this.dispatched) {
        return false;
      }
      return moment(this.arrival).day() > moment(this.dispatched).day();
    },
    workNextDay() {
      if (!this.departure || !this.arrival) {
        return false;
      }
      return moment(this.departure).day() > moment(this.arrival).day();
    },
    accepted: {
      get() {
        return this.acceptedTime;
      },
      set(val) {
        // accepted time is always on the scheduled day
        this.emitDateTime(
          "accepted_time",
          this.getDate(this.dispatchTime, false),
          val
        );
      }
    },
    dispatched: {
      get() {
        return this.dispatchTime;
      },
      set(val) {
        const isNextDay = this.isNextDay(this.acceptedTime, val);
        const date = this.getDate(this.acceptedTime, isNextDay);
        this.emitDateTime("dispatch_time", date, val);
      }
    },
    arrival: {
      get() {
        return this.arrivalTime;
      },
      set(val) {
        const isNextDay = this.isNextDay(this.dispatchTime, val);
        const date = this.getDate(this.dispatchTime, isNextDay);
        this.emitDateTime("arrival_time", date, val);
      }
    },
    departure: {
      get() {
        return this.departureTime;
      },
      set(val) {
        const isNextDay = this.isNextDay(this.arrivalTime, val);
        const date = this.getDate(this.arrivalTime, isNextDay);
        this.emitDateTime("departure_time", date, val);
      }
    },
    preworkLost() {
      return this.lostTimes.includes("accepted_time");
    },
    travelLost() {
      return this.lostTimes.includes("arrival_time");
    },
    workLost() {
      return this.lostTimes.includes("departure_time");
    },
    durationLost() {
      return this.lostTimes.includes("departure_time");
    },
    showWarning() {
      return this.lostTimes.length > 0;
    },
    openJobPage() {
      return this.jobShowPath(this.jobId) + "?create_appt=true";
    },
    travelStartedRequired() {
      // only require travel_started if no accepted_time exists during state of accepted
      if (this.apptState == "accepted") {
        return !this.accepted;
      } else {
        return !this.isDisabled("dispatch_time");
      }
    },
    showTenantPreWorkWarning() {
      const warningStates = ["accepted", "running", "completed"];
      if (
        this.settingEnabledForTenant("pre_work_enabled") &&
        (this.accepted == null || this.accepted == "") &&
        warningStates.includes(this.apptState)
      ) {
        return true;
      } else {
        return false;
      }
    }
  },
  validations() {
    return {
      accepted: {
        futureDate: mustNotBeFutureDate(
          () =>
            `Cannot use future time. Current time ${moment().format("h:mm a")}.`
        ),
        validAcceptedTime: helpers.withMessage(
          `Invalid Time Entered`,
          () => this.diffTimesInMIns(this.dispatched, this.accepted) >= 0
        ),
        $autoDirty: true
      },
      dispatched: {
        requiredIf: requiredIf(function () {
          return this.travelStartedRequired;
        }),
        futureDate: mustNotBeFutureDate(
          () =>
            `Cannot use future time. Current time ${moment().format("h:mm a")}.`
        ),
        validTripStartedTime: helpers.withMessage(
          `Invalid Time Entered`,
          () => {
            if (
              !this.isDisabled("arrival_time") &&
              !this.isDisabled("departure_time")
            )
              return (
                this.diffTimesInMIns(this.dispatched, this.accepted) >= 0 &&
                this.diffTimesInMIns(this.arrival, this.dispatched) >= 0
              );
            else
              return this.diffTimesInMIns(this.dispatched, this.accepted) >= 0;
          }
        )
      },
      arrival: {
        requiredIf: requiredIf(function () {
          return !this.isDisabled("arrival_time");
        }),
        futureDate: mustNotBeFutureDate(
          () =>
            `Cannot use future time. Current time ${moment().format("h:mm a")}.`
        ),
        validArrivalTime: helpers.withMessage(
          `Invalid Time Entered`,
          () => this.diffTimesInMIns(this.arrival, this.dispatched) >= 0
        ),
        $autoDirty: true
      },
      departure: {
        requiredIf: requiredIf(function () {
          return !this.isDisabled("departure_time");
        }),
        futureDate: mustNotBeFutureDate(
          () =>
            `Cannot use future time. Current time ${moment().format("h:mm a")}.`
        ),
        validDepartureTime: helpers.withMessage(
          `Invalid Time Entered`,
          () => this.diffTimesInMIns(this.departure, this.arrival) >= 0
        ),
        $autoDirty: true
      }
    };
  },
  watch: {
    accepted(newValue, oldValue) {
      if (newValue !== oldValue) {
        nextTick(() => {
          this.validateAllFields();
        });
      }
      // toggle showWarning when either new or old value is empty
      if (
        (newValue === "" || oldValue === "") &&
        this.settingEnabledForTenant("pre_work_enabled")
      ) {
        this.showTenantPreWorkWarning = !this.showTenantPreWorkWarning;
      }
    },
    dispatched(newValue, oldValue) {
      if (newValue !== oldValue) {
        nextTick(() => {
          this.validateAllFields();
        });
      }
    },
    arrival(newValue, oldValue) {
      if (newValue !== oldValue) {
        nextTick(() => {
          this.validateAllFields();
        });
      }
    },
    departure(newValue, oldValue) {
      if (newValue !== oldValue) {
        nextTick(() => {
          this.validateAllFields();
        });
      }
    }
  },
  created() {
    // fetch feature flags
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
  },
  methods: {
    shortDate,
    ...mapActions(["fetchTenant"]),
    generateValidationId(name) {
      this.ids[name] = this.ids[name]
        ? this.ids[name]
        : `${generateUID() + name}`;
      return this.ids[name];
    },
    async validateAllFields() {
      await this.v$.$validate();
    },
    isNextDay(date, time) {
      const prevTimeDate = moment(date);

      // The comparison time should use the same offset, but adjust the minutes & hours only
      const newTimeDate = modifyDate(prevTimeDate, { time });
      const prevTime = prevTimeDate.format("HH:mm").split(":");
      const newTime = newTimeDate.format("HH:mm").split(":");

      // Convert prevTime and selected time to military time
      // Check if the newTime is BEFORE the prevTime
      // if yes, then the user is trying to push travel/work to the next day
      return parseInt(prevTime[0]) > parseInt(newTime[0]);
    },
    getDate(prevDate, isNextDay = false) {
      // Return same day as scheduled_time or the next day
      const date = moment(prevDate || this.scheduledTime);
      if (isNextDay) {
        date.add(1, "day");
      }
      return date.format("MM/DD/YYYY");
    },
    emitDateTime(name, date, time) {
      const dateTime = time
        ? moment.tz(`${date} ${time}`, this.dateFormat, this.timezone).format()
        : time;
      this.$emit("change", name, dateTime);
    },
    timeIsLost(name) {
      return this.lostTimes.includes(name);
    },
    isDisabled(name) {
      return this.disabledFields.includes(name);
    },
    clearTime(time) {
      this[time] = "";
    },
    diffTimesInMIns(firstTime, secondTime) {
      if (!firstTime || !secondTime) return 0;
      return moment(firstTime).diff(secondTime, "minutes");
    },
    isInFuture(value) {
      return new Date(value) <= new Date();
    }
  }
};
</script>

<style lang="scss" scoped>
.lost-time {
  :deep(.date-picker-label) {
    color: var(--danger-color);
  }
  :deep(input) {
    border-color: var(--danger-color);
  }
}
.lost-calculated-time {
  color: var(--danger-color);
  text-decoration: line-through;
}
.warning-message {
  background: var(--warning-yellow);
  padding: 5px 10px;
  border-radius: 3px 3px 0 0;
}
.schedule-link {
  margin-left: 23px;
}
.calculated-time {
  margin-top: 33px;
}

.partial-border {
  &:after,
  &:before {
    content: "";
    background: var(--gray-border-color);
    position: absolute;
    right: 0;
    height: 39%;
    width: 2px;
  }

  &:after {
    top: 0;
  }
  &:before {
    height: 42%;
    bottom: 0;
  }
}

.date-to-display {
  margin-left: 2px;
  span {
    font-weight: bold;
  }
}
</style>
